<script setup lang="ts">
import { useAccountStore } from '@organisms/ubiq-administration/store/account';
import EmptyCardManager from '@organisms/ubiq-administration/subscriptions/EmptyCardManager.vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import type { Subscription } from '@models/subscription.ts';
import { currency } from '@elements/number-formatter';
import { trans } from '@elements/translation';
import { getCompleteShorterReadableDate } from '@tools/date';
import UbiqDataView from '@components/UbiqDataView.vue';
import IconArrowRight from '@icons/ph_arrow-right_black.svg';
import Icon from '@components/Icon.vue';
import { useSubscriptionStore } from '@organisms/ubiq-administration/store/subscriptions';
import CardEmptyTable from '../general/CardEmptyTable.vue';

const emit = defineEmits<{(e: 'createSubscription'): void;
}>();
const accountStore = useAccountStore();
const router = useRouter();
const { selectedSubscriptionData } = storeToRefs(useSubscriptionStore()) as {
  selectedSubscriptionData: Ref<Subscription | null>;
};

const goToSubscription = (subscription: Subscription): void => {
  if (!subscription) {
    return;
  }

  selectedSubscriptionData.value = subscription;
  router.push({ name: 'subscriptionDetails', params: { id: subscription.id } });
};
</script>

<template>
  <UbiqDataView>
    <template #list-item="{ item }">
      <div class="dataview__row" @click="goToSubscription(item)">
        <div class="dataview__row-informations">
          <div class="dataview-cell-flex">
            <span class="column--bold">
              {{ currency(item.amount / 100) }}
            </span>
            <span class="status-label" :class="`status-label--${item.status}`">
              {{ trans(`gestion.subscription.status.${item.status}`) }}
            </span>
          </div>
          <div>
            {{ item.client?.legal_name }}
          </div>
          <div>
            {{ getCompleteShorterReadableDate(item.start_date.iso) }}
          </div>
          <div>
            {{ item.end_date ? getCompleteShorterReadableDate(item.end_date?.iso) : '-' }}
          </div>
        </div>
        <div class="simple-table__column--actions">
          <Icon :svg="IconArrowRight" />
        </div>
      </div>
    </template>
    <template #empty-state>
      <EmptyCardManager
        :status="accountStore.stripeAccountStatus"
      >
        <template #ready>
          <CardEmptyTable
            logo="ph_visit-planned_blue"
            :title="trans('gestion.subscription.empty.title')"
            :subtitle="trans('gestion.subscription.empty.subtitle')"
            :button="trans('messaging.decision.stripe.subscription.create')"
            :status="accountStore.stripeAccountStatus"
            @on-click-empty-table="emit('createSubscription')"
          />
        </template>
      </EmptyCardManager>
    </template>
    <template #empty>
      {{ trans('gestion.subscription.step.1.no_result') }}
    </template>
  </UbiqDataView>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.status-label {
  margin-left: 8px;
}
</style>
